import { useState, useEffect, useMemo, useRef } from "react";
import { ArrowRight, TrendingUp } from "lucide-react";
import { useTranslation } from "react-i18next";

const SIZES = [5000, 10000, 25000, 50000, 100000, 250000];

// Smoothly count the number toward `value` without flashing the surrounding card.
function AnimatedNumber({ value, className = "", duration = 350 }) {
  const [display, setDisplay] = useState(value);
  const fromRef = useRef(value);
  const startRef = useRef(performance.now());
  const rafRef = useRef(null);
  useEffect(() => {
    fromRef.current = display;
    startRef.current = performance.now();
    const tick = (t) => {
      const elapsed = t - startRef.current;
      const u = Math.min(1, elapsed / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - u, 3);
      const next = fromRef.current + (value - fromRef.current) * eased;
      setDisplay(next);
      if (u < 1) rafRef.current = requestAnimationFrame(tick);
    };
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  return <span className={className}>${Math.round(display).toLocaleString()}</span>;
}

export default function ProfitCalculator() {
  const { t } = useTranslation();
  const [size, setSize] = useState(50000);
  const [rate, setRate] = useState(6);
  const split = 90;

  const monthly = useMemo(() => Math.round((size * (rate / 100) * (split / 100)) * 100) / 100, [size, rate]);
  const yearly = monthly * 12;
  const sizeLabel = size >= 1000 ? `$${(size / 1000).toLocaleString()}K` : `$${size}`;

  return (
    <section data-testid="profit-calculator" className="relative py-14 lg:py-28 overflow-hidden font-[Outfit]">
      {/* Removed the radial-gradient floor blob; the calculator card
          stands on the same continuous dark background as the rest of
          the page. */}

      <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <div className="pill-mono inline-flex mb-5" style={{ fontFamily: "Outfit, sans-serif" }}>{t("calculator.pill")}</div>
          <h2 className="text-3xl lg:text-5xl font-medium tracking-tight mb-4" style={{ fontFamily: "Outfit, sans-serif", letterSpacing: "-0.02em" }}>
            {t("calculator.title")} <span className="text-gradient-brand font-semibold">{t("calculator.titleAccent")}</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto text-sm lg:text-base font-light">
            {t("calculator.subtitle")}
          </p>
        </div>

        <div className="card-beam p-6 lg:p-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Sliders */}
          <div className="lg:col-span-7 space-y-8">
            {/* Account Size */}
            <div>
              <div className="flex justify-between items-end mb-3">
                <span className="text-[11px] text-zinc-500 uppercase tracking-widest font-light">{t("calculator.accountSize")}</span>
                <span className="text-3xl lg:text-4xl text-white font-medium tabular-nums">{sizeLabel}</span>
              </div>
              <input
                type="range"
                min={5000}
                max={250000}
                step={5000}
                value={size}
                onChange={(e) => setSize(Number(e.target.value))}
                data-testid="calc-size-slider"
                className="w-full accent-brand"
                style={{ accentColor: "#AADEEB" }}
              />
              <div className="flex justify-between mt-2 text-[12px] text-zinc-500 font-light">
                {SIZES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setSize(s)}
                    data-testid={`calc-size-${s}`}
                    className={`px-2 py-0.5 rounded transition-colors tabular-nums ${size === s ? "text-brand font-medium" : "hover:text-zinc-300"}`}
                  >
                    {s >= 1000 ? `$${s / 1000}K` : `$${s}`}
                  </button>
                ))}
              </div>
            </div>

            {/* Profit Rate */}
            <div>
              <div className="flex justify-between items-end mb-3">
                <span className="text-[11px] text-zinc-500 uppercase tracking-widest font-light">{t("calculator.monthlyRate")}</span>
                <span className="text-3xl lg:text-4xl text-white font-medium tabular-nums">{rate}%</span>
              </div>
              <input
                type="range"
                min={1}
                max={15}
                step={1}
                value={rate}
                onChange={(e) => setRate(Number(e.target.value))}
                data-testid="calc-rate-slider"
                className="w-full"
                style={{ accentColor: "#AADEEB" }}
              />
              <div className="flex justify-between text-[12px] text-zinc-500 mt-2 font-light">
                <span>{t("calculator.conservative")} · 1%</span>
                <span>{t("calculator.realistic")} · 6%</span>
                <span>{t("calculator.aggressive")} · 15%</span>
              </div>
            </div>
          </div>

          {/* Take-home result — static card, only the number updates */}
          <div className="lg:col-span-5">
            <div
              className="relative rounded-2xl p-6 lg:p-8 overflow-hidden"
              style={{
                background: "linear-gradient(135deg, rgba(170,222,235,0.08) 0%, rgba(170,222,235,0.02) 100%)",
                border: "1px solid rgba(170,222,235,0.18)",
              }}
            >
              <TrendingUp className="absolute top-6 right-6 text-brand/40" size={32} />
              <span className="text-[10px] text-brand tracking-widest uppercase font-light">{t("calculator.takeHome")}</span>
              <div className="mt-2 mb-1">
                <AnimatedNumber
                  value={monthly}
                  className="text-5xl lg:text-6xl font-bold text-gradient-brand block leading-none tabular-nums"
                />
              </div>
              <div className="text-[11px] text-zinc-500 mb-5 font-light">{t("calculator.basedOn")}</div>

              <div className="space-y-2 pt-5 border-t border-white/10">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500 font-light">{t("calculator.perYear")}</span>
                  <AnimatedNumber value={yearly} className="text-white font-medium tabular-nums" />
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-500 font-light">{t("calculator.grossMonthly")}</span>
                  <AnimatedNumber value={size * rate / 100} className="text-white font-medium tabular-nums" />
                </div>
              </div>

              <a
                href="#challenges"
                data-testid="calc-cta"
                className="btn-solid-brand w-full mt-6 text-sm justify-center"
                style={{ fontFamily: "Outfit, sans-serif" }}
              >
                {t("calculator.cta")} <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
